import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import {
  BRAND_KIT,
  LOGO_VARIANTS,
} from "@/constants/brandKitTestIds";
import {
  Field,
  TextInput,
  TextArea,
  NumberInput,
  BindingPillGroup,
  MultiSelectChips,
  FormActions,
} from "./Primitives";
import useBindings from "@/lib/useBindings";
import useContentCategories from "@/lib/useContentCategories";

const PATH = "logos";

const EMPTY = {
  name: "",
  variant: "primary_lockup",
  image_url: null,
  allowed_backgrounds: [],
  allowed_background_color_ids: [],
  usable_in_category_slugs: [],
  binding_slugs: [],
  min_clear_space_multiplier: null,
  min_width_inches: null,
  notes: "",
  priority: 100,
};

export default function LogosTab({ brandId, canManage }) {
  const [items, setItems] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | <logo_id>
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const { bindings, loading: bindingsLoading } = useBindings("logo", brandId);
  const { categories } = useContentCategories(brandId);
  const colorOptions = useMemo(
    () => colors.map((c) => ({ value: c.color_id, label: c.name, swatch: c.hex })),
    [colors],
  );
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.slug, label: c.name })),
    [categories],
  );

  const load = async () => {
    setLoading(true);
    const [logosRes, colorsRes] = await Promise.all([
      api.get(`/brands/${brandId}/brand-kit/${PATH}`),
      api.get(`/brands/${brandId}/brand-kit/colors`),
    ]);
    setItems(logosRes.data);
    setColors(colorsRes.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [brandId]);

  const openNew = () => {
    setForm({ ...EMPTY, priority: (items.length + 1) * 10 });
    setEditing("new");
    setError("");
  };
  const openEdit = (it) => {
    setForm({
      name: it.name,
      variant: it.variant,
      image_url: it.image_url,
      allowed_backgrounds: it.allowed_backgrounds || [],
      allowed_background_color_ids: it.allowed_background_color_ids || [],
      usable_in_category_slugs: it.usable_in_category_slugs || [],
      binding_slugs: it.binding_slugs || [],
      min_clear_space_multiplier: it.min_clear_space_multiplier,
      min_width_inches: it.min_width_inches,
      notes: it.notes || "",
      priority: it.priority ?? 100,
    });
    setEditing(it.logo_id);
    setError("");
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (editing === "new") {
        await api.post(`/brands/${brandId}/brand-kit/${PATH}`, form);
      } else {
        await api.put(`/brands/${brandId}/brand-kit/${PATH}/${editing}`, form);
      }
      close();
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save logo");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (logo_id) => {
    try {
      await api.delete(`/brands/${brandId}/brand-kit/${PATH}/${logo_id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not delete logo");
    }
  };

  if (loading) return <p className="text-sm text-[#6B6358]">Loading logos…</p>;

  return (
    <div>
      <Header
        title="Logos"
        subtitle="Primary lockups, wordmarks and approved variants. Use Notes to capture do-not-modify guidance."
        canManage={canManage}
        actionTestId={BRAND_KIT.logoAddButton}
        onAdd={openNew}
        active={editing !== null}
      />

      {editing !== null && (
        <form onSubmit={submit} className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6 mb-6">
          <h3 className="font-serif text-lg mb-4">
            {editing === "new" ? "New logo asset" : "Edit logo asset"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name">
              <TextInput
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testid={BRAND_KIT.logoNameInput}
                required
                placeholder="e.g. Main Logo — Primary Lockup"
              />
            </Field>
            <Field label="Variant">
              <select
                data-testid={BRAND_KIT.logoVariantSelect}
                value={form.variant}
                onChange={(e) => setForm({ ...form, variant: e.target.value })}
                className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
              >
                {LOGO_VARIANTS.map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Image" className="md:col-span-2">
              <ImageUpload
                value={form.image_url || ""}
                onChange={(url) => setForm({ ...form, image_url: url || null })}
                brandId={brandId}
                testIdSuffix="brandkit-logo"
              />
            </Field>
            <Field label="Min clear space (× cap height)">
              <NumberInput
                value={form.min_clear_space_multiplier}
                onChange={(v) => setForm({ ...form, min_clear_space_multiplier: v })}
                testid={BRAND_KIT.logoClearSpaceInput}
                step="0.05"
                placeholder="e.g. 0.5"
              />
            </Field>
            <Field label="Min width (inches)">
              <NumberInput
                value={form.min_width_inches}
                onChange={(v) => setForm({ ...form, min_width_inches: v })}
                testid={BRAND_KIT.logoMinWidthInput}
                step="0.05"
                placeholder="e.g. 0.75"
              />
            </Field>
            <Field label="Allowed backgrounds (comma-separated)" className="md:col-span-2">
              <TextInput
                value={(form.allowed_backgrounds || []).join(", ")}
                onChange={(v) =>
                  setForm({
                    ...form,
                    allowed_backgrounds: v.split(",").map((s) => s.trim()).filter(Boolean),
                  })
                }
                testid={BRAND_KIT.logoBackgroundsInput}
                placeholder="white, cream, brand-teal"
              />
            </Field>
            <Field label="Notes" className="md:col-span-2">
              <TextArea
                value={form.notes}
                onChange={(v) => setForm({ ...form, notes: v })}
                testid={BRAND_KIT.logoNotesInput}
                rows={2}
                placeholder="Usage rules, do-nots, approval contacts…"
              />
            </Field>
            <Field label="Layout bindings (where this logo is used)" className="md:col-span-2">
              <BindingPillGroup
                bindings={bindings}
                loading={bindingsLoading}
                selected={form.binding_slugs}
                onChange={(v) => setForm({ ...form, binding_slugs: v })}
                testidPrefix={BRAND_KIT.logoBindingPrefix}
              />
            </Field>
          </div>

          <div className="border-t border-[#1A1F2C]/10 mt-6 pt-5">
            <div className="flex items-baseline justify-between mb-1">
              <h4 className="font-serif text-base">AI Assembly disambiguation</h4>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#B5462F]">advanced</span>
            </div>
            <p className="text-xs text-[#6B6358] mb-4 max-w-2xl">
              Used by the AI Assembly Agent when multiple logos share a binding.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Priority (lower = picked first)">
                <NumberInput
                  value={form.priority}
                  onChange={(v) => setForm({ ...form, priority: v ?? 100 })}
                  testid={BRAND_KIT.logoPriorityInput}
                  step="1"
                  placeholder="100"
                />
              </Field>
              <div />
              <Field label="Allowed background colors (from this brand's palette)" className="md:col-span-2">
                <MultiSelectChips
                  options={colorOptions}
                  selected={form.allowed_background_color_ids}
                  onChange={(v) => setForm({ ...form, allowed_background_color_ids: v })}
                  testidPrefix={BRAND_KIT.logoAllowedBgPrefix}
                  emptyLabel="Author colors first to use them as background constraints."
                />
              </Field>
              <Field label="Restrict to content categories (optional)" className="md:col-span-2">
                <MultiSelectChips
                  options={categoryOptions}
                  selected={form.usable_in_category_slugs}
                  onChange={(v) => setForm({ ...form, usable_in_category_slugs: v })}
                  testidPrefix={BRAND_KIT.logoCategoryPrefix}
                  emptyLabel="No content categories defined for this brand yet."
                />
              </Field>
            </div>
          </div>

          {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}
          <FormActions
            submitting={submitting}
            onCancel={close}
            submitTestId={BRAND_KIT.logoSubmit}
            cancelTestId={BRAND_KIT.logoCancel}
          />
        </form>
      )}

      {items.length === 0 ? (
        <Empty testid={BRAND_KIT.logosEmpty} label="No logos yet." />
      ) : (
        <div data-testid={BRAND_KIT.logosList} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div
              key={it.logo_id}
              data-testid={`${BRAND_KIT.logoRowPrefix}${it.logo_id}`}
              className="border border-[#1A1F2C]/10 bg-white rounded-lg p-5"
            >
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-md bg-[#F8F5EE] border border-[#1A1F2C]/10 flex items-center justify-center overflow-hidden">
                  {it.image_url ? (
                    <img src={it.image_url} alt={it.name} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B6358]">No image</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-1">
                    <div className="font-serif text-lg leading-tight">{it.name}</div>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] shrink-0">P {it.priority ?? 100}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#B5462F] mt-1">
                    {LOGO_VARIANTS.find((v) => v.value === it.variant)?.label || it.variant}
                  </div>
                  {(it.min_clear_space_multiplier || it.min_width_inches) && (
                    <div className="text-xs text-[#6B6358] mt-2">
                      {it.min_clear_space_multiplier ? `Clear space ≥ ${it.min_clear_space_multiplier}×` : null}
                      {it.min_clear_space_multiplier && it.min_width_inches ? " · " : ""}
                      {it.min_width_inches ? `Min ${it.min_width_inches}in wide` : null}
                    </div>
                  )}
                  {it.binding_slugs?.length > 0 && (
                    <div className="text-xs text-[#6B6358] mt-1">Bindings: {it.binding_slugs.join(", ")}</div>
                  )}
                  {it.usable_in_category_slugs?.length > 0 && (
                    <div className="text-[11px] text-[#6B6358] italic">Categories: {it.usable_in_category_slugs.join(", ")}</div>
                  )}
                  {it.notes && <p className="text-xs text-[#3D3A35] mt-2 line-clamp-2">{it.notes}</p>}
                </div>
              </div>
              {canManage && (
                <RowActions
                  onEdit={() => openEdit(it)}
                  onDelete={() => remove(it.logo_id)}
                  editTestId={`${BRAND_KIT.logoEditPrefix}${it.logo_id}`}
                  deleteTestId={`${BRAND_KIT.logoDeletePrefix}${it.logo_id}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ title, subtitle, canManage, onAdd, actionTestId, active }) {
  return (
    <div className="flex items-start justify-between mb-6 gap-6">
      <div>
        <h2 className="font-serif text-2xl">{title}</h2>
        <p className="text-sm text-[#6B6358] mt-1 max-w-xl">{subtitle}</p>
      </div>
      {canManage && !active && (
        <button
          data-testid={actionTestId}
          onClick={onAdd}
          className="shrink-0 px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm"
        >
          + Add
        </button>
      )}
    </div>
  );
}

function Empty({ testid, label }) {
  return (
    <div data-testid={testid} className="border border-dashed border-[#1A1F2C]/15 rounded-lg px-6 py-10 text-sm text-[#6B6358] text-center">
      {label}
    </div>
  );
}

function RowActions({ onEdit, onDelete, editTestId, deleteTestId }) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  const onDeleteClick = () => {
    if (!armed) { setArmed(true); return; }
    setArmed(false);
    onDelete();
  };

  return (
    <div className="flex gap-2 mt-4 pt-3 border-t border-[#1A1F2C]/10 items-center">
      <button data-testid={editTestId} onClick={onEdit} className="text-xs px-3 py-1.5 rounded-full border border-[#1A1F2C]/20 hover:border-[#1A1F2C]/40">
        Edit
      </button>
      <button
        data-testid={deleteTestId}
        onClick={onDeleteClick}
        className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
          armed
            ? "bg-[#B5462F] text-[#F8F5EE]"
            : "text-[#B5462F] hover:bg-[#B5462F]/5"
        }`}
      >
        {armed ? "Confirm delete" : "Delete"}
      </button>
      {armed && (
        <button
          type="button"
          onClick={() => setArmed(false)}
          className="text-xs text-[#6B6358] hover:text-[#1A1F2C]"
        >
          Cancel
        </button>
      )}
    </div>
  );
}

export { Header as _Header, Empty as _Empty, RowActions as _RowActions };
